import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Shield, Loader2, AlertTriangle } from "lucide-react";

const DISCLAIMER = `Florida Sportsman is a listing platform only. We do not sell, own, ship, or inspect any firearm, ammunition, or accessory listed on the marketplace.

By using the firearms marketplace, you acknowledge and agree that:

1. YOU are solely responsible for verifying that every firearm, ammunition, magazine, suppressor, or NFA item transaction you initiate complies with all applicable federal, Florida state, and local laws — including but not limited to background checks, waiting periods, FFL transfers, NFA tax stamps, magazine capacity limits, and buyer/seller residency requirements.

2. Buyer and seller assume ALL liability for the legality, safety, and outcome of every transaction. Florida Sportsman assumes NO liability for any illegal action, injury, damage, or loss arising from any transaction facilitated through this platform.

3. You are of legal age (18+ for long guns and ammunition, 21+ for handguns and NFA items) and legally permitted under federal and Florida law to buy, sell, or possess the items involved.

4. Providing false age or identity information is a violation of these terms and may be reported to law enforcement.

5. Florida Sportsman is a proud supporter of the Second Amendment and the NRA — and equally firm about lawful ownership.`;

export default function FirearmsVerify() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/marketplace";

  const [dob, setDob] = useState("");
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!accept) return toast.error("You must accept the disclaimer");
    if (!dob) return toast.error("Enter your date of birth");
    setSubmitting(true);
    try {
      const { data } = await api.post("/users/me/verify-age", { dob, accept_terms: accept });
      setUser(data);
      toast.success("Age verified");
      navigate(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  const already = !!user.firearms_disclaimer_accepted_at;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 animate-fade-in" data-testid="firearms-verify-page">
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-secondary mb-2">
        <Shield className="w-3.5 h-3.5" /> Firearms marketplace
      </div>
      <h1 className="font-display font-extrabold text-3xl md:text-4xl">Age & liability verification</h1>
      <p className="text-muted-foreground mt-3">
        One-time step. Required before you can buy, sell or trade any firearm, ammo, or NFA item.
        {already && (
          <span className="block text-primary mt-2">You've already verified — you can update your DOB below if needed.</span>
        )}
      </p>

      <div className="mt-6 bg-secondary/5 border-l-4 border-secondary rounded-r-md p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
        <p className="text-sm leading-relaxed">
          Lying about your age or eligibility is a violation of these terms and may be reported to law enforcement.
          The buyer and seller assume all legal liability for every transaction.
        </p>
      </div>

      <form onSubmit={submit} className="mt-8 bg-card border border-border rounded-md p-6 md:p-8 space-y-5">
        <div className="max-w-xs">
          <Label htmlFor="dob">Date of birth</Label>
          <Input
            id="dob"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            required
            className="mt-1.5"
            data-testid="input-dob"
            max={new Date().toISOString().split("T")[0]}
          />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Full disclaimer</Label>
          <div
            className="mt-1.5 border border-border rounded-md bg-muted/40 p-4 h-56 overflow-y-auto text-sm whitespace-pre-line leading-relaxed"
            data-testid="disclaimer-body"
          >
            {DISCLAIMER}
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer" data-testid="label-accept">
          <Checkbox
            checked={accept}
            onCheckedChange={(v) => setAccept(!!v)}
            className="mt-0.5"
            data-testid="check-accept"
          />
          <span className="text-sm leading-relaxed">
            I confirm the date of birth above is mine and truthful, and I accept the full disclaimer, terms of service,
            and community rules. I understand Florida Sportsman assumes no liability for illegal transactions.
          </span>
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="btn-cancel-verify">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting || !accept || !dob}
            className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
            data-testid="btn-submit-verify"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Verify & continue
          </Button>
        </div>
      </form>
    </div>
  );
}
